import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import aivyLogo from "../../images/aivy_logo3.png";
import { apiClient } from "../../utils/api";

const GoogleCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const hasProcessed = useRef(false);
  
  const signIn = useAuthStore((state) => state.signIn);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      // 이미 처리 중이면 중복 실행 방지
      if (hasProcessed.current) {
        return;
      }
      
      hasProcessed.current = true;
      try {
        const code = searchParams.get("code");
        const error = searchParams.get("error");

        if (error) {
          throw new Error("구글 로그인이 취소되었습니다.");
        }

        if (!code) {
          throw new Error("인증 코드를 받지 못했습니다.");
        }

        // 세션에서 PKCE 값들 가져오기
        const codeVerifier = sessionStorage.getItem("g_code_verifier") || "";
        const nonce = sessionStorage.getItem("g_nonce") || "";
        const redirectUrl = sessionStorage.getItem("g_redirect_url") || `${window.location.origin}/auth/google/callback`;

        if (!codeVerifier || !nonce) {
          throw new Error("로그인 세션이 만료되었습니다. 다시 시도해주세요.");
        }

        // 약관 동의 정보가 있는지 확인 
        const agreements = sessionStorage.getItem("social_agreements");
        
        if (agreements) {
          // 약관 동의 정보가 있으면 바로 회원가입 API 호출
          const agreementData = JSON.parse(agreements);
          
          const signupResponse = await apiClient.post("/api/v1/auth/google_signup_20250916", {
            code: code,
            redirect_url: redirectUrl,
            code_verifier: codeVerifier,
            nonce: nonce,
            private_agreement: agreementData.private_agreement,
            service_agreeement: agreementData.service_agreeement,
            marketing_agreement: agreementData.marketing_agreement,
          });

          const signupResult = signupResponse.data;

          if (!signupResult.status) {
            throw new Error(signupResult.error || "구글 회원가입에 실패했습니다.");
          }

          // 회원가입 성공 
          signIn(
            {
              accessToken: signupResult.access_token,
              refreshToken: signupResult.refresh_token,
            },
            signupResult.data
          );
          
          sessionStorage.removeItem("social_agreements");
          navigate("/");
          return;
        } else {
          // 약관 동의 정보가 없으면 로그인 API 호출
          const response = await apiClient.post("/api/v1/auth/google_signin_20250916", {
            code: code,
            redirect_url: redirectUrl,
            code_verifier: codeVerifier,
            nonce: nonce,
          });

          const result = response.data;

          if (!result.status) {
            throw new Error(result.error || "구글 로그인에 실패했습니다.");
          }

          if (result.not_user) {
            // 가입된 유저가 없는 경우 소셜 회원가입 페이지로 이동
            navigate("/auth/social-signup", {
              state: {
                platform: "google",
                redirectUrl: redirectUrl,
                codeVerifier: codeVerifier,
                nonce: nonce,
              },
            });
            return;
          }

          signIn(
            {
              accessToken: result.access_token,
              refreshToken: result.refresh_token,
            },
            result.data
          );
          
          navigate("/");
        }
      } catch (err: any) {
        console.error("구글 콜백 처리 오류:", err);
        setError(err.message || "구글 로그인 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
        sessionStorage.removeItem("g_code_verifier");
        sessionStorage.removeItem("g_nonce");
        sessionStorage.removeItem("g_redirect_url");
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate, signIn]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <img
              src={aivyLogo}
              alt="aivy ai studio logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">구글 로그인 처리 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-4">
            <img
              src={aivyLogo}
              alt="aivy ai studio logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            <p className="font-medium">로그인 실패</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
          <button
            onClick={() => navigate("/auth/signin")}
            className="px-6 py-3 bg-pink-500 text-white font-medium rounded-lg hover:bg-pink-600 transition-all"
          >
            로그인 페이지로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleCallback;
